import React, { useEffect, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import notificationService from 'services/notificationService';
import { reportService } from 'api/services';
import type { VoucherViewDto } from 'api/types';

// ============================================================================
// VOUCHER VIEW — read-only view of one posted voucher, any class: header and
// every line. The last level of the Trial Balance drill-down (TB → ledger →
// voucher). Opening the original document (invoice, receipt, payment) is a
// later step.
// ============================================================================

const money = (n?: number | null) => (n ? n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '');
const fmtDate = (s?: string | null) => (s ? new Date(s).toLocaleDateString() : '');
const muted: React.CSSProperties = { color: 'var(--text-muted)' };
const th: React.CSSProperties = { textAlign: 'left', padding: '0.4rem 0.6rem', fontWeight: 600 };
const td: React.CSSProperties = { padding: '0.35rem 0.6rem', verticalAlign: 'top' };

export interface VoucherViewModalProps {
  transactionId: string | null;
  onClose: () => void;
  /** Loads the voucher; defaults to the Trial Balance endpoint (authorised by that report). */
  load?: (transactionId: string) => Promise<VoucherViewDto>;
}

export const VoucherViewModal: React.FC<VoucherViewModalProps> = ({ transactionId, onClose, load }) => {
  const [voucher, setVoucher] = useState<VoucherViewDto | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!transactionId) { setVoucher(null); return; }
    let live = true;
    setLoading(true);
    (load ?? reportService.getTrialBalanceVoucher)(transactionId)
      .then(v => { if (live) setVoucher(v); })
      .catch(err => {
        notificationService.toast.error(err?.response?.data?.message || 'Could not load the voucher.');
        onClose();
      })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transactionId]);

  if (!transactionId) return null;
  const v = voucher;
  const fc = v?.lines.some(l => l.fcAmount && l.currencyCode && l.currencyCode !== v.currencyCode) || (v && v.exchangeRate !== 1);
  const field = (label: string, value?: React.ReactNode) => (
    <div><div style={{ ...muted, fontSize: '0.72rem' }}>{label}</div><div style={{ fontSize: '0.85rem' }}>{value || '—'}</div></div>
  );

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-xl" style={{ minHeight: 'auto' }}>
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">
            {v ? `${v.voucherTypeName || v.voucherClass} ${v.transactionNo}` : 'Voucher'}
            {v?.isLocked && <span className="badge" style={{ marginLeft: '0.5rem', background: '#64748b', color: '#fff', fontSize: '0.7rem' }}
              title={v.lockSource || undefined}>Locked</span>}
          </h2>
          <button className="modal-close" onClick={onClose}><IconSystem.x size={20} /></button>
        </div>
        <div className="modal-content">
          {loading && <div style={{ ...muted, padding: '1rem' }}>Loading…</div>}
          {v && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.75rem', marginBottom: '0.9rem' }}>
                {field('Date', fmtDate(v.transactionDate))}
                {field('Party', v.partyName)}
                {field('Reference', v.reference)}
                {field('Business unit', v.businessUnitName)}
                {field('Currency', v.currencyCode ? `${v.currencyCode}${v.exchangeRate !== 1 ? ` @ ${v.exchangeRate}` : ''}` : '')}
                {field('Source', v.sourceModule)}
                {field('Status', v.status)}
                {field('Entered', `${v.createdAt ? new Date(v.createdAt).toLocaleString() : ''}${v.createdByName ? ` · ${v.createdByName}` : ''}`)}
              </div>
              {v.narration && <div style={{ fontSize: '0.82rem', marginBottom: '0.75rem' }}><span style={muted}>Narration: </span>{v.narration}</div>}
              <div style={{ border: '1px solid var(--border-color)', borderRadius: '0.5rem', overflow: 'auto', maxHeight: '50vh' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
                  <thead>
                    <tr style={{ background: 'var(--grid-header-bg, #dbeafe)' }}>
                      <th style={{ ...th, width: 40 }}>#</th>
                      <th style={th}>Ledger</th>
                      <th style={th}>Party / narration</th>
                      <th style={{ ...th, width: 120 }}>Business unit</th>
                      {fc && <th style={{ ...th, width: 120, textAlign: 'right' }}>Foreign amount</th>}
                      <th style={{ ...th, width: 120, textAlign: 'right' }}>Debit</th>
                      <th style={{ ...th, width: 120, textAlign: 'right' }}>Credit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {v.lines.map(l => (
                      <tr key={l.lineNo} style={{ borderTop: '1px solid var(--border-color)' }}>
                        <td style={{ ...td, ...muted }}>{l.lineNo}</td>
                        <td style={td}>{l.ledgerName}<div style={{ ...muted, fontSize: '0.72rem' }}>{l.ledgerCode}</div></td>
                        <td style={td}>{[l.partyName, l.narration].filter(Boolean).join(' · ') || '—'}</td>
                        <td style={{ ...td, ...muted }}>{l.businessUnitName}</td>
                        {fc && <td style={{ ...td, textAlign: 'right' }}>{l.fcAmount ? `${l.currencyCode ?? ''} ${money(Math.abs(l.fcAmount))}` : ''}</td>}
                        <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{money(l.drAmount)}</td>
                        <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{money(l.crAmount)}</td>
                      </tr>
                    ))}
                    <tr style={{ borderTop: '2px solid var(--border-color)', fontWeight: 700 }}>
                      <td style={td} colSpan={fc ? 5 : 4}>Total</td>
                      <td style={{ ...td, textAlign: 'right' }}>{money(v.totalDebit)}</td>
                      <td style={{ ...td, textAlign: 'right' }}>{money(v.totalCredit)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </>
  );
};

export default VoucherViewModal;
